<template>
  <div :class="fieldClass" v-show="!field.hidden">
    <slot name="pre-field"></slot>
    <slot>
      <sup v-if="fieldLabelIsSuperscript" :class="labelClass" :for="`${id}-tooltip-target`">{{ labelText }}</sup>
      <sub v-else-if="fieldLabelIsSubscript" :class="labelClass" :for="`${id}-tooltip-target`">{{ labelText }}</sub>
      <label v-else-if="fieldLabelIsPlain" :class="labelClass" :for="`${id}-tooltip-target`">{{ labelText }}</label>
      <form-control-store
        v-if="field.type !== 'label'"
        :id="`${id}-tooltip-target`"
        :path="path"
        :group-read-only="groupReadOnly"
        @change="onChange($event)"
        @input="onInput($event)"
        v-validate="validations"
        :type="field.type"
      />
      <form-control-store v-else :path="path" :group-read-only="groupReadOnly" :type="field.type" />
    </slot>
    <el-alert
      v-if="veeValidationErrors && veeValidationErrors.has(validationName)"
      :type="alertType"
      :closable="false"
      :title="parsedErrorMessage"
      show-icon
      name="post-field"
    />
  </div>
</template>
<script>
import FormControlStore from './FormControlStore'
import { ERROR_MSG_TYPE, randomString } from '../../utils'
import { isCheckbox, isInfo, isLink } from '../../form'
import EventBus from '../../utils/event-bus'
import parseError from '../../utils/mixin-parse-error-message'
import { get, isEmpty, find, isEqual } from 'lodash'
import { FIELD_PROPS, FIELD_REQUIRED_LEVELS } from '../../consts'

const { dateFields } = require('../../form')

/**
 * @class FormFieldStore
 *
 * @description This component is the state handler/wrapper for FormGroup
 */
export default {
  name: 'FormFieldStore',
  components: {
    FormControlStore,
  },
  props: {
    path: {
      type: String,
    },
    groupReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [parseError],
  methods: {
    refreshValidator() {
      this.$validator.detach(this.id)
      this.$validator.detach(this.validationName)
      this.$validator.attach({
        name: this.validationName,
        rules: this.validations,
      })
    },
    onValidate($event) {
      if (isEmpty(get($event, 'value', '')) || !this.validationName) {
        return
      }
      this.$nextTick(() => {
        if (
          !isEmpty(
            find(this.$validator._base.fields.items, {
              name: this.validationName,
            })
          )
        ) {
          this.$validator
            .validate(this.validationName, this.validationValue)
            .then(() => EventBus.$emit('errors-changed', this.validationName))
        }
      })
    },
    onInvalid($event) {
      this.onValidate($event)
    },
    onChange($event) {
      this.onValidate($event)
    },
    onInput($event) {
      if ('.,-+'.indexOf($event.data) < 0) {
        this.onValidate($event)
      }
    },
  },
  computed: {
    fieldClass() {
      const classNames = ['form-field']
      if (this.isRequired && !this.computedReadOnly) {
        classNames.push('required')
      }
      if (get(this.field, 'errors.length')) {
        classNames.push('has-error')
      }
      return classNames.join(' ')
    },
    isRequired() {
      return [FIELD_REQUIRED_LEVELS.WARNING, FIELD_REQUIRED_LEVELS.ERROR].includes(this.requiredErrorLevel)
    },
    requiredErrorLevel() {
      return get(this.validations, FIELD_PROPS.REQUIRED, FIELD_REQUIRED_LEVELS.NONE)
    },
    validations() {
      return get(this.field, 'validations', {})
    },
    computedReadOnly() {
      return Boolean(this.field.readOnly || this.groupReadOnly)
    },
    field() {
      return this.$get('fieldByPath')(this.path)
    },
    fieldFormats() {
      return isEmpty(this.field.formats) ? {} : this.field.formats
    },
    labelClass() {
      let classNames = ['control-label']
      if (this.groupReadOnly) classNames.push('readOnly')
      if (isInfo(this.field)) classNames.push('invisible')
      if (this.isRequired && !this.computedReadOnly) classNames.push('required')
      if (this.fieldFormats) {
        if (this.fieldFormats.bold) classNames.push('bold')
        if (this.fieldFormats.italic) classNames.push('italic')
        if (this.fieldFormats.underline) classNames.push('underline')
        if (this.fieldFormats.strike) classNames.push('strikethrough')
      }
      return classNames.join(' ')
    },
    id() {
      if (this.field && this.field.id) {
        return this.field.id
      } else if (this.field && this.field.name) {
        return `id-${this.field.name}`
      }
      return `id-${randomString()}-${Date.now()}`
    },
    fieldLabelIsSuperscript() {
      return this.labelText && this.fieldFormats.superscript
    },
    fieldLabelIsSubscript() {
      return this.labelText && this.fieldFormats.subscript
    },
    fieldLabelIsPlain() {
      return this.labelText && !isLink(this.field) && !isCheckbox(this.field)
    },
    labelText() {
      return this.$i18n.t(this.field.label) // || this.label) @todo this.label???
    },
    parsedErrorMessage() {
      const msg = this.veeValidationErrors.first(this.validationName)
      if (!msg) {
        return null
      } else {
        const { text, params } = this.createMessageObject({
          msg,
          name: this.validationName,
          label: this.field.label,
          fieldType: this.field.type,
        })
        Object.keys(params).forEach((key) => {
          if ('label' === key) {
            params[key] = this.$i18n.t(params[key])
          }
        })
        return this.$i18n.interpolate(this.$i18n.t(text), params)
      }
    },
    validationName() {
      return `${this.$storeNamespace}-${this.idOrName}`
    },
    validationValue() {
      let value = this.field.value
      if (value) {
        if (dateFields.includes(this.field.type)) {
          if (this.field.type === 'time') {
            return new Date().setHours(this.field.value.split(':')[0], this.field.value.split(':')[1])
          } else {
            return new Date(this.field.value).getTime()
          }
        } else if (this.field.type === 'number' && typeof value === 'string') {
          return Number(value.replace(/\s/g, '').replace(',', '.'))
        } else {
          return value
        }
      } else {
        return this.field.value
      }
    },
    idOrName() {
      return this.field.id || this.field.name
    },
    alertType() {
      return this.requiredErrorLevel === FIELD_REQUIRED_LEVELS.ERROR ? ERROR_MSG_TYPE.ERROR : ERROR_MSG_TYPE.WARNING
    },
  },
  mounted() {
    this.refreshValidator()
    //Listen on the EventBus for the parent component running validation
    EventBus.$on('frontendValidate', this.onValidate)
    //Watch for the changes to the childs error bag and pass back to the parent
    this.$watch(
      () => this.$validator.errors,
      (newValue, oldValue) => {
        EventBus.$emit('errors-changed', newValue)
      }
    )
  },
  beforeDestroy() {
    this.$validator.detach(this.validationName)
    EventBus.$emit('errors-changed', this.validationName)
    EventBus.$off('frontendValidate', this.onValidate)
  },
  watch: {
    validations: {
      handler(newVal, oldVal) {
        if (!isEqual(newVal, oldVal)) {
          this.refreshValidator()
        }
      },
    },
  },
}
</script>
<style lang="scss" scoped>
.bold {
  font-weight: bold !important;
}
.italic {
  font-style: italic !important;
}
.underline {
  text-decoration: underline !important;
}
.strikethrough {
  text-decoration: line-through !important;
}
.control-label.readOnly {
  padding: 10px 0px 0px 0px;
  margin-bottom: 0.5em;
}
.control-label {
  font-weight: 700;
  color: $color-text-regular;
  padding: 10px 0px;
  font-size: 14px;
}

label.required:before {
  content: '*';
  color: $color-not-set;
  margin-right: 5px;
}
</style>
